import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const DeliveryTrackingPanel = ({ deliveryLogs, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const getStatusColor = (status) => {
    const colors = {
      delivered: 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400',
      sent: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400',
      pending: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400',
      failed: 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
  };

  const getStatusIcon = (status) => {
    const icons = {
      delivered: 'CheckCircle',
      sent: 'Send',
      pending: 'Clock',
      failed: 'XCircle'
    };
    return icons?.[status] || 'Mail';
  };

  const filteredLogs = deliveryLogs?.filter(log => {
    const matchesSearch = !searchTerm || 
      log?.recipient_email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      log?.subject?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = !statusFilter || log?.notification_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon name="Mail" size={24} className="text-blue-600 dark:text-blue-400" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Real-Time Delivery Monitoring</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Chronological email logs with delivery status and timestamps</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            placeholder="Search by email or subject..."
            className="w-full px-4 py-2 border border-neutral-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e?.target?.value)}
          className="px-4 py-2 border border-neutral-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
        >
          <option value="">All Statuses</option>
          <option value="delivered">Delivered</option>
          <option value="sent">Sent</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Icon name="RefreshCw" size={18} />
          Refresh
        </button>
      </div>

      {/* Delivery Logs Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-slate-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Recipient
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Subject
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Sent At
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Delivered At
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Error Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
            {filteredLogs?.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No delivery logs found
                </td>
              </tr>
            ) : (
              filteredLogs?.map((log) => (
                <tr key={log?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                  <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                    {log?.recipient_email || 'N/A'}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                    {log?.subject || 'No Subject'}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(log?.notification_status)}`}>
                      <Icon name={getStatusIcon(log?.notification_status)} size={14} />
                      {log?.notification_status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {log?.sent_at ? new Date(log?.sent_at)?.toLocaleString() : 'Not sent'}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {log?.delivered_at ? new Date(log?.delivered_at)?.toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {log?.error_message || log?.notification_delivery_log?.[0]?.error_details || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DeliveryTrackingPanel;